/**
 * Shared helpers for the editable "Footnotes" row appended to SummaryGrid —
 * one merged, editable cell per plan group (spanning that group's plan
 * columns).
 */

import type { ColDef } from "ag-grid-community";

// Identifies a synthesized "awaiting quote" placeholder plan group (ADR-024)
// that has no persisted proposal row yet — committing a footnote on one must
// materialize it into a real proposal plan group carrying that footnote.
export interface FootnotePlaceholderTarget {
  carrierId: string;
  carrierName: string;
  rfpBenefitPlanGroupId: string;
  planPosition: number;
}

export type FootnoteTarget =
  | { kind: "proposal"; id: string }
  | { kind: "benefit"; id: string }
  | { kind: "placeholder"; target: FootnotePlaceholderTarget };

export const footnoteTargetForGroup = (
  group: {
    proposalPlanGroupId?: string;
    benefitPlanGroupId?: string;
  },
  placeholderTarget?: FootnotePlaceholderTarget,
): FootnoteTarget | undefined => {
  if (group.proposalPlanGroupId) {
    return { kind: "proposal", id: group.proposalPlanGroupId };
  }
  if (group.benefitPlanGroupId) {
    return { kind: "benefit", id: group.benefitPlanGroupId };
  }
  if (placeholderTarget) {
    return { kind: "placeholder", target: placeholderTarget };
  }
  return undefined;
};

export const FOOTNOTE_ROW_KEY = "__isFootnoteRow";

// Makes the first column the footnote anchor: on the footnote row its cell spans
// `span` leaf columns to its right (the editable input itself is rendered by
// the grid's cell renderer). Used at plan-group scope (span = the group's own
// columns). Non-footnote rows keep the column's normal colSpan.
export const withFootnoteSpan = <T extends ColDef>(
  children: T[],
  span: number,
): T[] => {
  if (children.length === 0) return children;
  const [first, ...rest] = children;
  const ownColSpan = first.colSpan;
  return [
    {
      ...first,
      cellClassRules: {
        ...first.cellClassRules,
        "rates-footnote-cell": (params: { data?: Record<string, unknown> }) =>
          Boolean(params.data?.[FOOTNOTE_ROW_KEY]),
      },
      colSpan: (params) =>
        params.data?.[FOOTNOTE_ROW_KEY]
          ? span
          : typeof ownColSpan === "function"
            ? ownColSpan(params)
            : 1,
    },
    ...rest,
  ];
};

export const withFootnoteAnchor = <T extends ColDef>(children: T[]): T[] =>
  withFootnoteSpan(children, children.length);
